import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  AlertTriangle,
  Megaphone,
  Bell,
  Info,
  CalendarDays,
  Sun,
  Clock3,
  ScrollText,
} from "lucide-react";

dayjs.extend(relativeTime);

export const NOTIFICATION_TYPES = [
  { value: "GENERAL", label: "General" },
  { value: "ANNOUNCEMENT", label: "Announcement" },
  { value: "ALERT", label: "Alert" },
  { value: "EVENT", label: "Event" },
  { value: "HOLIDAY", label: "Holiday" },
  { value: "REMINDER", label: "Reminder" },
  { value: "POLICY", label: "Policy" },
];

export const NOTIFICATION_AUDIENCES = [
  { value: "TEACHERS", label: "All teachers" },
  { value: "PARENTS", label: "All parents" },
  { value: "ALL", label: "All teachers & parents" },
  { value: "CUSTOM", label: "Selected teachers & parents" },
  { value: "CLASSROOM", label: "Specific class" },
];

export const NOTIFICATION_AUDIENCE_MODES = [
  {
    value: "TEACHERS",
    label: "All teachers",
    description: "Send to every active teacher account.",
  },
  {
    value: "PARENTS",
    label: "All parents",
    description: "Send to every active parent account.",
  },
  {
    value: "ALL",
    label: "Everyone",
    description: "Broadcast to all teachers and parents.",
  },
  {
    value: "CUSTOM",
    label: "Selected people",
    description: "Choose specific teachers and/or parents.",
  },
  {
    value: "CLASSROOM",
    label: "Class",
    description: "Parents in one class plus the homeroom teacher.",
  },
];

export function audienceLabel(sendTo, notification) {
  if (sendTo === "CUSTOM") {
    const teachers = notification?.targetTeachers?.length || 0;
    const parents = notification?.targetParents?.length || 0;
    const parts = [];
    if (teachers) parts.push(`${teachers} teacher${teachers === 1 ? "" : "s"}`);
    if (parents) parts.push(`${parents} parent${parents === 1 ? "" : "s"}`);
    return parts.length ? `Selected: ${parts.join(", ")}` : "Selected recipients";
  }
  if (sendTo === "CLASSROOM") {
    const name = notification?.targetClassroom?.classroomName;
    return name ? `Class: ${name}` : "Specific class";
  }
  return NOTIFICATION_AUDIENCES.find((item) => item.value === sendTo)?.label || "Recipients";
}

export function deliveryStatusLabel(status) {
  if (status === "QUEUED") return "Queued";
  if (status === "PROCESSING") return "Sending";
  if (status === "COMPLETED") return "Delivered";
  if (status === "FAILED") return "Failed";
  return "";
}

export function deliveryStatusTone(status) {
  if (status === "QUEUED") return "is-queued";
  if (status === "PROCESSING") return "is-processing";
  if (status === "COMPLETED") return "is-completed";
  if (status === "FAILED") return "is-failed";
  return "";
}

export function deliverySummary(notification) {
  const stats = notification?.deliveryStats || {};
  if (!notification?.deliveryStatus) return "";
  return `${stats.sent || 0}/${stats.total || 0} sent`;
}

export function notificationTypeLabel(type) {
  return NOTIFICATION_TYPES.find((item) => item.value === type)?.label || type || "—";
}

export function typeBadgeClass(type) {
  if (type === "ALERT") return "is-alert";
  if (type === "ANNOUNCEMENT") return "is-announcement";
  if (type === "EVENT") return "is-event";
  if (type === "HOLIDAY") return "is-holiday";
  if (type === "REMINDER") return "is-reminder";
  if (type === "POLICY") return "is-policy";
  if (type === "GENERAL") return "is-general";
  return "is-notification";
}

export function typeIcon(type) {
  // Keep Ionicons mapping in parent/teacher Utils/noticeTypes in sync with these.
  if (type === "ALERT") return AlertTriangle;
  if (type === "ANNOUNCEMENT") return Megaphone;
  if (type === "EVENT") return CalendarDays;
  if (type === "HOLIDAY") return Sun;
  if (type === "REMINDER") return Clock3;
  if (type === "POLICY") return ScrollText;
  if (type === "GENERAL") return Info;
  return Bell;
}

export function typeAccent(type) {
  if (type === "ALERT") {
    return { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" };
  }
  if (type === "ANNOUNCEMENT") {
    return { bg: "#fffbeb", color: "#d97706", border: "#fde68a" };
  }
  if (type === "EVENT") {
    return { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" };
  }
  if (type === "HOLIDAY") {
    return { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" };
  }
  if (type === "REMINDER") {
    return { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" };
  }
  if (type === "POLICY") {
    return { bg: "#f8fafc", color: "#475569", border: "#cbd5e1" };
  }
  if (type === "GENERAL") {
    return { bg: "#f0f9ff", color: "#0284c7", border: "#bae6fd" };
  }
  return { bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" };
}

export function showcaseClass(type) {
  return typeBadgeClass(type);
}

export function readLabel(isRead) {
  return isRead ? "Read" : "Unread";
}

export function readTone(isRead) {
  return isRead ? "is-read" : "is-unread";
}

export function snippet(text = "", max = 120) {
  const value = String(text || "").trim().replace(/\s+/g, " ");
  if (!value) return "No message content.";
  return value.length > max ? `${value.slice(0, max).trim()}…` : value;
}

/** Short one-line preview for table cells (word-based). */
export function wordSnippet(text = "", maxWords = 8) {
  const value = String(text || "").trim().replace(/\s+/g, " ");
  if (!value) return "No message content.";
  const words = value.split(" ");
  if (words.length <= maxWords) return value;
  return `${words.slice(0, maxWords).join(" ")}…`;
}

export function formatCreatedAt(value) {
  if (!value) return "—";
  const date = dayjs(value);
  if (!date.isValid()) return "—";
  return date.format("MMM D, YYYY · h:mm A");
}

export function relativeCreatedAt(value) {
  if (!value) return "";
  const date = dayjs(value);
  if (!date.isValid()) return "";
  return date.fromNow();
}

export function countUnreadInDocs(docs = []) {
  return docs.filter((item) => !item.isRead).length;
}
